<template>
  <el-dialog
    :visible.sync="isFormDialogVisible"
    custom-class="fifl-dialog"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    :append-to-body="true"
    top="20px"
  >
    <div
      slot="title"
      class="dialog-header"
    >
      <h2>
        {{ isModify ? '홀딩 수정' : '홀딩 등록' }}
      </h2>
    </div>
    <el-form
      label-position="top"
      label-width="100px"
    >
      <el-row :gutter="15">
        <el-col :span="24">
          <el-row :gutter="15">
            <el-col :span="12">
              <el-form-item label="홀딩 시작일">
                <el-date-picker
                  class="responsive-visible desktop"
                  v-model="form.start"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder="시작일"
                  :picker-options="startDatePickerOption"
                />

                <input-date-mobile
                  class="responsive-visible mobile"
                  :minimum-date="childMembership.start"
                  :maximum-date="childMembership.end"
                  minimum-date-warning-message="회원권 시작일 이전 날짜는 선택할 수 없습니다."
                  maximum-date-warning-message="회원권 종료일 이후 날짜는 선택할 수 없습니다."
                  v-model="form.start"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="홀딩 종료일">
                <el-date-picker
                  class="responsive-visible desktop"
                  v-model="form.end"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder="종료일"
                  :picker-options="endDatePickerOption"
                />

                <input-date-mobile
                  class="responsive-visible mobile"
                  :minimum-date="form.start || childMembership.start"
                  :maximum-date="childMembership.end"
                  :minimum-date-warning-message="endDateMinimumMessage"
                  :is-use-maximum-date-filter="false"
                  v-model="form.end"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </el-col>
      </el-row>
      <el-row :gutter="15">
        <el-col :span="24">
          <el-form-item label="홀딩 사유">
            <el-input
              v-model="form.memo"
              maxlength="30"
              placeholder="홀딩 사유 입력 (30자 이내)"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <dialog-action-buttons
      ref="dialogActionButtons"
      slot="footer"
      @cancel="closeDialog"
      @confirm="handleClickSaveForm"
    />

    <booking-exist-warning-dialog
      :title="validates.bookingExist.title"
      :message="validates.bookingExist.message"
      :dialog-visible="validates.bookingExist.dialogVisible"
      :bookings="bookings"
      :cancel-button-text="validates.bookingExist.buttonText"
      confirm-button-text="확인"
      @confirm="onCancelValidateDialog('bookingExist')"
      @cancel="onConfirmValidateDialog('bookingExist')"
    />
  </el-dialog>
</template>

<script>
import dateFns from 'date-fns';
import FormDialog from '@/components/AbstractComponents/FormDialog.vue';
import DialogActionButtons from '@/components/dialogActionButtons';
import BookingExistWarningDialog from '@/components/BookingExistWarningDialog';
import dialogValidateFormMixin from '@/mixins/dialogValidateFormMixin.js';
import { getBookingsAndWaitingInRange } from '@/utils/date-helper';
import { formResponseErrorHandler, HOLDING_CREATE_FAIL_MESSAGES, HOLDING_UPDATE_FAIL_MESSAGES } from '@/fail-message-handler';
import InputDateMobile from '@/components/InputDateMobile';
import { fiflboxApi } from '@/utils/api';

function makeBookingExistMessage(start, end, isHoldingExist, isModifying) {
  const actionName = isHoldingExist ? '수정' : '등록';
  const bookingSystemWord = isModifying ? '예약 내역' : '수강 내역';
  const startDateString = dateFns.format(start, 'YYYY년 M월 D일');
  const endDateString = dateFns.format(end, 'YYYY년 M월 D일');

  return {
    title: `홀딩 바로 ${actionName}이 불가합니다.`,
    buttonText: `모든 ${bookingSystemWord} 먼저 취소하고 홀딩 ${actionName}`,
    message: `홀딩하고자 한 기간인 <strong>${startDateString} - ${endDateString}</strong>에
이미 ${bookingSystemWord}이 존재합니다. 아래의 모든 ${bookingSystemWord}을
취소하시고 홀딩 ${actionName}을 완료해주시길 바랍니다.`,
  };
}

function makeBookingExistMessageForShrink(start, end) {
  const startDateString = dateFns.format(start, 'YYYY년 M월 D일');
  const endDateString = dateFns.format(end, 'YYYY년 M월 D일');

  return {
    title: '홀딩 바로 수정이 불가합니다.',
    buttonText: '모든 예약 내역 먼저 취소하고 홀딩 수정',
    message: `홀딩 수정 시 업데이트될 회원권 기간인 <strong>${startDateString} - ${endDateString}</strong>에 포함되지 않은 예약 내역이 존재합니다.
아래의 모든 예약 내역을 취소하시고 홀딩 수정을 완료해주시길 바랍니다. 취소 시 수강 횟수가 자동 복구됩니다.`,
  };
}

export default {
  name: 'HoldingFormDialog',
  components: {
    InputDateMobile,
    BookingExistWarningDialog,
    DialogActionButtons,
  },
  extends: FormDialog,
  mixins: [dialogValidateFormMixin],
  props: [
    'childMembershipId', 'data', 'memberships',
  ],
  data() {
    return {
      bookings: [],
      isFormDialogVisible: false,
      isModify: false,
      originHoldingDuration: null,
      form: {
        id: null,
        memo: '',
        start: null,
        end: null,
        duration: null,
        child_membership: null,
      },
      childMembership: {
        start: null,
        end: null,
      },
      warningMessage: {
        startEmpty: '홀딩 시작일을 입력해주세요.',
        endEmpty: '홀딩 종료일을 입력해주세요.',
        bothEmpty: '홀딩 기간을 설정해주세요.',
        startBiggerThanEnd : '홀딩 시작일은 종료일보다 뒤로 설정할 수 없습니다. 홀딩 기간을 조정해 주세요.',
        insertTitle: '날짜 입력',
        resetTitle : '홀딩 불가',
      },
      validates: {
        bookingExist: {
          title: '',
          message: '',
          buttonText: '',
          validateFunction: 'validateBookingExist',
          isValidated: false,
          dialogVisible: false,
        },
      },
    };
  },
  computed: {
    isBookingSystem() {
      return this.$store.getters.getIsBookingSystem;
    },
    startDatePickerOption() {
      let { start, end } = this.childMembership;
      return {
        disabledDate(time) {
          return !dateFns.isWithinRange(time, start, end);
        },
      };
    },
    endDatePickerOption() {
      let { start, end } = this.childMembership;
      let holdingStart = this.form.start;

      return {
        disabledDate(time) {
          if (holdingStart) {
            return dateFns.isBefore(time, holdingStart);
          }

          return dateFns.isBefore(time, start);
        },
      };
    },
    endDateMinimumMessage() {
      if(this.form.start) {
        return '시작일 이전 날짜는 선택할 수 없습니다.';
      }

      return '회원권 시작일 이전 날짜는 선택할 수 없습니다.';
    },
  },
  methods: {
    clearData() {
      this.form = {
        id: null,
        memo: '',
        start: null,
        duration: null,
        child_membership: null,
      };
    },
    openDialogToAdd(childMembership) {
      this.clearData();
      this.isModify = false;
      this.form.child_membership = childMembership.id;
      this.childMembership.start = childMembership.start;
      this.childMembership.end = childMembership.end;
      this.isFormDialogVisible = true;
    },
    openDialogToModify(data, childMembership) {
      this.clearData();
      this.isModify = true;
      this.form = Object.assign({}, data);
      this.originHoldingDuration = dateFns.differenceInDays(data.end, data.start);
      this.childMembership.start = childMembership.start;
      this.childMembership.end = childMembership.end;
      this.isFormDialogVisible = true;
    },
    closeDialog() {
      this.isFormDialogVisible = false;
    },

    async syncBookings() {
      let { data: bookings } = await fiflboxApi.v1.getBookings({
        child_membership: this.form.child_membership,
        status: [
          'BOOKED', 'PASS', 'WAITING',
        ],
      });

      return bookings;
    },
    setBookingExistDialogMessage() {
      const bookingExistMessage = makeBookingExistMessage(
        this.form.start,
        this.form.end,
        !!this.form.id,
        this.isBookingSystem,
      );

      this.validates.bookingExist.title = bookingExistMessage.title;
      this.validates.bookingExist.message = bookingExistMessage.message;
      this.validates.bookingExist.buttonText = bookingExistMessage.buttonText;
    },
    setBookingExistDialogMessageForShrink(membershipStart, expectEndDay) {
      const bookingExistMessage = makeBookingExistMessageForShrink(membershipStart, expectEndDay);

      this.validates.bookingExist.title = bookingExistMessage.title;
      this.validates.bookingExist.message = bookingExistMessage.message;
      this.validates.bookingExist.buttonText = bookingExistMessage.buttonText;
    },
    validateBookingExist() {
      // 알림으로 알려줘야하는 예약 => 수정 or 생성하는 케이스만 신경쓰면됨
      // 홀딩이 생성되는 기간에 이미 예약들이 존재하는 경우 -> this.form.start와 this.form.end로 필터
      // 홀딩이 더 짧은 기간으로 수정될 때 같이 줄어드는 회원권 기간에 예약이 존재하는 경우 -> (this.childMembership.end - (originDurtaion - newDuration)), this.childMembership.end
      // 홀딩이 더 넓은 기간으로 수정될 때 홀딩이 넓어지는 기간에 예약이 존재하는 경우 -> this.form.start와 this.form.end로 필터

      const newHoldingDuration = dateFns.differenceInDays(this.form.end, this.form.start);

      if (!!this.form.id && (this.originHoldingDuration > newHoldingDuration)) {
        const expectEndDay = dateFns.subDays(this.childMembership.end, (this.originHoldingDuration - newHoldingDuration - 1));

        this.bookings = getBookingsAndWaitingInRange(
          this.bookings,
          expectEndDay,
          this.childMembership.end,
        );

        this.setBookingExistDialogMessageForShrink(this.childMembership.start, expectEndDay);
        return this.bookings.length > 0;
      }

      this.bookings = getBookingsAndWaitingInRange(
        this.bookings,
        this.form.start,
        this.form.end,
      );
      this.setBookingExistDialogMessage();
      return this.bookings.length > 0;
    },

    async handleClickSaveForm() {
      this.form.enrollment = this.enrollmentId;
      this.bookings = await this.syncBookings();
      if(!this.form.start && !this.form.end){
        this.$alert(this.warningMessage.bothEmpty, this.warningMessage.insertTitle);
      }else if(!this.form.start){
        this.$alert(this.warningMessage.startEmpty, this.warningMessage.insertTitle);
      }else if(!this.form.end){
        this.$alert(this.warningMessage.endEmpty, this.warningMessage.insertTitle);
      }else if(dateFns.isAfter(this.form.start, this.form.end)){
        this.$alert(this.warningMessage.startBiggerThanEnd, this.warningMessage.resetTitle);
      }else{
        this.validateForm();
      }
    },

    async requestCreateHolding(form) {
      try {
        const { data } = await fiflboxApi.v2.postMembershipHoldings(form);

        return {
          isSuccess: true,
          data,
        };
      } catch (err) {
        return {
          isSuccess: false,
          error: err,
        };
      }
    },
    async requestUpdateHolding(form) {
      try {
        const { data } = await fiflboxApi.v2.putMembershipHoldings(form.id, form);

        return {
          isSuccess: true,
          data,
        };
      } catch (err) {
        return {
          isSuccess: false,
          error: err,
        };
      }
    },
    showHoldingDuration({ duration }, isModifying) {
      const actionName = isModifying ? '수정' : '등록';

      this.$message({
        type: 'success',
        message: `총 ${duration}일 홀딩 ${actionName}이 완료됐습니다. `,
      });
    },
    async createHolding() {
      const { isSuccess, error, data } = await this.requestCreateHolding(this.form);

      if (!isSuccess) {
        await formResponseErrorHandler(HOLDING_CREATE_FAIL_MESSAGES, error);
        return false;
      }

      this.showHoldingDuration(data, false);
      return true;
    },
    async updateHolding() {
      const { isSuccess, error, data } = await this.requestUpdateHolding(this.form);

      if (!isSuccess) {
        await formResponseErrorHandler(HOLDING_UPDATE_FAIL_MESSAGES, error);
        return false;
      }

      this.showHoldingDuration(data, true);
      return true;
    },
    async submitForm() {
      const result = this.form.id ?
        await this.updateHolding() : await this.createHolding();
        
      if (!result) {
        await this.$refs['dialogActionButtons'].stopLoading();
        return;
      }

      await this.closeDialog();
      await this.$refs['dialogActionButtons'].stopLoading();
      await this.$emit('update');
    },
    // onConfirmBookingValidateDialog() {
    //   this.closeBookingValidateDialog();
    //   this.submitForm();
    // },
    // closeBookingValidateDialog(validateName) {
    //   this.bookingExistDialogVisible = false;
    // },
  },
};
</script>

<style scoped>
</style>
